export const apiIntegrationModule = {
  id: "api-integration-enterprise",
  title: "Enterprise API Integration Patterns",
  description: "Master integration strategies for connecting enterprise systems, handling data transformation, and managing distributed transactions.",
  content: `
## What You'll Learn
By the end of this module, you will be able to:
- Implement enterprise integration patterns for system connectivity
- Design and manage data transformation between heterogeneous systems
- Handle distributed transactions and maintain data consistency
- Build resilient integration layers with proper error handling and retry mechanisms

## Prerequisites
- Experience with REST APIs and web services
- Understanding of database systems and data modeling
- Basic knowledge of message queues and event-driven architecture

## Overview
Enterprise systems integration is critical for business operations, with 83% of IT leaders citing integration challenges as a major obstacle to digital transformation. This module covers proven integration patterns used by Fortune 500 companies.

---

## Key Concepts

### Integration Patterns

#### Point-to-Point Integration
**Direct System Connections**: Simple but creates tight coupling
**Use Cases**: Limited system count, simple data flows
**Challenges**: Scalability, maintenance complexity

#### Hub-and-Spoke Architecture
**Central Integration Hub**: All systems connect through a central point
**Benefits**: Centralized control, easier monitoring
**Drawbacks**: Single point of failure, potential bottleneck

#### Enterprise Service Bus (ESB)
**Message-Oriented Middleware**: Decoupled system communication
**Service Orchestration**: Workflow management and business process automation
**Protocol Translation**: Convert between different message formats and protocols

### Data Transformation Strategies

#### Schema Mapping
**Field-Level Mapping**: Direct attribute correspondence between systems
**Data Type Conversion**: Handle different data formats and types
**Business Rule Application**: Transform data according to business logic

#### Extract, Transform, Load (ETL)
**Batch Processing**: Scheduled data synchronization
**Real-Time Streaming**: Continuous data flow processing
**Change Data Capture**: Track and propagate only modified data

### Distributed Transaction Management

#### Two-Phase Commit (2PC)
**Coordinator Pattern**: Ensures atomicity across multiple systems
**Prepare Phase**: All participants prepare for transaction
**Commit Phase**: All participants execute or rollback

#### Saga Pattern
**Compensating Transactions**: Handle failures through reversal operations
**Choreography**: Decentralized coordination
**Orchestration**: Centralized workflow management

---

## Practical Examples

### Example 1: Customer Data Synchronization
Synchronize customer data between CRM, ERP, and e-commerce systems.

**Integration Architecture**:
- Message broker for async communication
- Data transformation layer for format standardization
- Conflict resolution for concurrent updates
- Audit trail for data lineage tracking

**Implementation Strategy**:
Use event-driven architecture with change data capture to propagate customer updates across all systems in real-time.

### Example 2: Order Processing Workflow
Orchestrate multi-system order processing across inventory, payment, and fulfillment systems.

**Workflow Steps**:
1. Inventory check and reservation
2. Payment processing and authorization
3. Fulfillment initiation and tracking
4. Customer notification and updates

**Error Handling**:
Implement compensating transactions for each step to handle failures gracefully and maintain system consistency.

### Example 3: Legacy System Modernization
Gradually replace legacy systems while maintaining business continuity.

**Strangler Fig Pattern**:
- Route new functionality to modern APIs
- Gradually migrate legacy data and processes
- Maintain backward compatibility during transition
- Sunset legacy systems incrementally

---

## Hands-On Lab

### Lab: Multi-System Integration Platform
Build an integration platform connecting multiple enterprise systems.

**System Components**:
1. Customer Management System (CRM)
2. Enterprise Resource Planning (ERP)
3. E-commerce Platform
4. Inventory Management System

**Integration Requirements**:
- Real-time customer data synchronization
- Order processing workflow across all systems
- Inventory updates with conflict resolution
- Comprehensive error handling and monitoring

**Technical Implementation**:
- Message broker (Apache Kafka or RabbitMQ)
- API gateway for routing and transformation
- Database for integration state management
- Monitoring and alerting system

**Data Flow Design**:
Create event-driven data flows with proper error handling, retry mechanisms, and dead letter queues.

---

## Assessment

### Integration Checklist
- [ ] Integration patterns appropriately selected for use case
- [ ] Data transformation handles all edge cases
- [ ] Error handling includes proper retry and fallback mechanisms
- [ ] Transaction management ensures data consistency
- [ ] Monitoring provides visibility into integration health
- [ ] Documentation covers data flow and error scenarios
- [ ] Performance testing validates system scalability
- [ ] Security measures protect data in transit

### Performance Considerations
**Throughput Optimization**: Batch processing, connection pooling, and async operations
**Latency Reduction**: Caching, data locality, and efficient protocols
**Reliability**: Circuit breakers, timeouts, and graceful degradation

This module prepares you to design and implement enterprise-grade integration solutions that scale with business needs.
`,
  duration: 90,
  codeExamples: [
    {
      title: "Event-Driven Integration with Message Broker",
      language: "javascript",
      code: `// Customer data synchronization across systems
const kafka = require('kafkajs');

class CustomerIntegrationService {
  constructor() {
    this.kafka = kafka({
      clientId: 'customer-integration',
      brokers: [process.env.KAFKA_BROKER]
    });
    this.producer = this.kafka.producer();
    this.consumer = this.kafka.consumer({ groupId: 'customer-sync' });
  }

  async publishCustomerUpdate(customerId, updateData, source) {
    const event = {
      eventId: generateEventId(),
      eventType: 'CUSTOMER_UPDATED',
      timestamp: new Date().toISOString(),
      source: source,
      data: {
        customerId,
        changes: updateData,
        version: await this.getCustomerVersion(customerId)
      }
    };

    await this.producer.send({
      topic: 'customer-events',
      messages: [{
        key: customerId,
        value: JSON.stringify(event),
        headers: {
          'event-type': 'CUSTOMER_UPDATED',
          'source-system': source
        }
      }]
    });
  }

  async handleCustomerEvent(event) {
    const { customerId, changes, version, source } = event.data;
    
    try {
      // Version conflict detection
      const currentVersion = await this.getCustomerVersion(customerId);
      if (version <= currentVersion) {
        console.log('Event already processed or out of order');
        return;
      }

      // Transform data for target systems
      const transformedData = await this.transformCustomerData(changes, source);
      
      // Update all connected systems except source
      const targetSystems = this.getTargetSystems().filter(sys => sys !== source);
      
      await Promise.all(targetSystems.map(async (system) => {
        try {
          await this.updateSystemCustomer(system, customerId, transformedData[system]);
          await this.updateCustomerVersion(customerId, version);
        } catch (error) {
          // Send to dead letter queue for manual resolution
          await this.sendToDeadLetterQueue(event, system, error);
        }
      }));

    } catch (error) {
      console.error('Error processing customer event:', error);
      throw error;
    }
  }
}`
    },
    {
      title: "Saga Pattern for Distributed Transactions",
      language: "javascript",
      code: `// Order processing saga with compensating transactions
class OrderProcessingSaga {
  constructor() {
    this.steps = [
      { name: 'reserveInventory', compensate: 'releaseInventory' },
      { name: 'processPayment', compensate: 'refundPayment' },
      { name: 'createShipment', compensate: 'cancelShipment' },
      { name: 'sendConfirmation', compensate: 'sendCancellation' }
    ];
  }

  async executeOrder(orderData) {
    const sagaId = generateSagaId();
    const context = { orderId: orderData.id, sagaId, completedSteps: [] };
    
    try {
      for (const step of this.steps) {
        console.log('Executing step:', step.name);
        
        const result = await this[step.name](orderData, context);
        context.completedSteps.push({ 
          name: step.name, 
          result, 
          timestamp: new Date() 
        });
        
        // Persist saga state for recovery
        await this.persistSagaState(sagaId, context);
      }
      
      await this.markSagaComplete(sagaId);
      return { success: true, orderId: orderData.id };
      
    } catch (error) {
      console.error('Saga execution failed:', error);
      await this.executeCompensation(context);
      throw new Error('Order processing failed: ' + error.message);
    }
  }

  async executeCompensation(context) {
    console.log('Starting compensation for saga:', context.sagaId);
    
    // Execute compensating transactions in reverse order
    for (let i = context.completedSteps.length - 1; i >= 0; i--) {
      const step = context.completedSteps[i];
      const compensationStep = this.steps.find(s => s.name === step.name);
      
      try {
        await this[compensationStep.compensate](step.result, context);
        console.log('Compensation completed for:', step.name);
      } catch (compensationError) {
        console.error('Compensation failed for:', step.name, compensationError);
        // Log for manual intervention
        await this.logCompensationFailure(context.sagaId, step, compensationError);
      }
    }
  }

  async reserveInventory(orderData, context) {
    const reservation = await inventoryService.reserve({
      items: orderData.items,
      orderId: orderData.id,
      timeout: 300000 // 5 minutes
    });
    return reservation;
  }

  async releaseInventory(reservation, context) {
    await inventoryService.release(reservation.id);
  }

  async processPayment(orderData, context) {
    const payment = await paymentService.charge({
      amount: orderData.total,
      currency: orderData.currency,
      customer: orderData.customerId,
      orderId: orderData.id
    });
    return payment;
  }

  async refundPayment(payment, context) {
    await paymentService.refund(payment.id);
  }
}`
    }
  ],
  practice: {
    title: "Build Multi-System Integration",
    description: "Create an integration layer that synchronizes data between CRM, ERP, and e-commerce systems.",
    starter: `// Simple direct integration (tightly coupled)
class CustomerService {
  async updateCustomer(customerId, updateData) {
    // Update local database
    await database.updateCustomer(customerId, updateData);
    
    // Direct API calls to other systems
    await crmAPI.updateCustomer(customerId, updateData);
    await erpAPI.updateCustomer(customerId, updateData);
    await ecommerceAPI.updateCustomer(customerId, updateData);
  }
}`,
    solution: `// Event-driven integration with proper error handling
class CustomerIntegrationService {
  constructor() {
    this.eventBus = new EventBus();
    this.transformationService = new DataTransformationService();
    this.retryService = new RetryService();
  }

  async updateCustomer(customerId, updateData, sourceSystem) {
    const transaction = await this.beginTransaction();
    
    try {
      // Update source system
      await this.updateSourceSystem(sourceSystem, customerId, updateData);
      
      // Publish integration event
      const event = {
        eventId: generateEventId(),
        eventType: 'CUSTOMER_UPDATED',
        timestamp: new Date().toISOString(),
        source: sourceSystem,
        data: { customerId, updateData, version: Date.now() }
      };
      
      await this.eventBus.publish('customer-updates', event);
      await transaction.commit();
      
    } catch (error) {
      await transaction.rollback();
      throw error;
    }
  }

  async handleCustomerUpdateEvent(event) {
    const { customerId, updateData, source } = event.data;
    const targetSystems = this.getTargetSystems(source);
    
    for (const targetSystem of targetSystems) {
      await this.retryService.execute(async () => {
        // Transform data for target system
        const transformedData = await this.transformationService
          .transform(updateData, source, targetSystem);
        
        // Update target system
        await this.updateTargetSystem(targetSystem, customerId, transformedData);
        
        // Log successful integration
        await this.auditService.log('INTEGRATION_SUCCESS', {
          source, target: targetSystem, customerId
        });
        
      }, {
        maxRetries: 3,
        backoffStrategy: 'exponential',
        onFailure: async (error) => {
          await this.handleIntegrationFailure(event, targetSystem, error);
        }
      });
    }
  }

  async handleIntegrationFailure(event, targetSystem, error) {
    // Send to dead letter queue for manual resolution
    await this.deadLetterService.send({
      originalEvent: event,
      targetSystem,
      error: error.message,
      timestamp: new Date().toISOString()
    });
    
    // Alert operations team
    await this.alertService.send({
      type: 'INTEGRATION_FAILURE',
      message: 'Customer sync failed',
      details: { targetSystem, customerId: event.data.customerId }
    });
  }
}`
  },
  quiz: {
    question: "Which integration pattern provides the best decoupling between enterprise systems?",
    options: [
      "Point-to-Point Integration",
      "Hub-and-Spoke Architecture",
      "Event-Driven Architecture with Message Broker",
      "Direct Database Integration"
    ],
    correctAnswer: 2,
    explanation: "Event-Driven Architecture with Message Broker provides the best decoupling by allowing systems to communicate asynchronously through events without direct dependencies."
  }
};